import type { PbjModelUpsertBody, PbjPrediction } from "../../../utils/models";

/** The persisted row's CMI pair — the baseline an "edited since load" check measures against. */
export type SavedCmi = { pccPdpmAvg: number; natNursingCaseMixIndex: number };

export type CasperCmi = {
	/**
	 * True when the backend scored with CASPER's CMI pair AND the admin hasn't touched it since
	 * load — i.e. the effective values below are CASPER's, not the draft's.
	 */
	active: boolean;
	/** The pair the scorer actually used: CASPER's when `active`, otherwise the draft's. */
	pccPdpmAvg: number;
	natNursingCaseMixIndex: number;
	/** pccPdpmAvg ÷ natNursingCaseMixIndex — 0 when the denominator is 0. */
	cmi: number;
};

/**
 * Resolves which CMI pair the UI should display.
 *
 * The draft is compared against the SAVED row rather than the national defaults: a CASPER pair
 * may already be persisted on the row, so a defaults comparison would read "admin edited it" for
 * every CASPER-backed facility. A manually-pinned row (`cmiManual`) never displays CASPER values.
 */
export const resolveCasperCmi = (prediction: PbjPrediction, draft: PbjModelUpsertBody, savedCmi: SavedCmi): CasperCmi => {
	const active =
		prediction.cmiSource === "casper" &&
		!draft.cmiManual &&
		draft.pccPdpmAvg === savedCmi.pccPdpmAvg &&
		draft.natNursingCaseMixIndex === savedCmi.natNursingCaseMixIndex;

	const pccPdpmAvg = active ? prediction.config.pccPdpmAvg : draft.pccPdpmAvg;
	const natNursingCaseMixIndex = active ? prediction.config.natNursingCaseMixIndex : draft.natNursingCaseMixIndex;

	return { active, pccPdpmAvg, natNursingCaseMixIndex, cmi: natNursingCaseMixIndex ? pccPdpmAvg / natNursingCaseMixIndex : 0 };
};
